import { useState, useEffect } from 'react';
import { AlertTriangle, TrendingUp, Zap, BookOpen, MessageSquare, Save, Library, Video } from 'lucide-react';
import { UserUsage, UsageInfo, TIER_DISPLAY_NAMES, UserTier } from '../types/tier';

interface UsageDisplayProps {
    className?: string;
    compact?: boolean;
}

interface UsageBarProps {
    label: string;
    icon: React.ReactNode;
    usage: UsageInfo;
    showResetTime?: boolean;
}

function UsageBar({ label, icon, usage, showResetTime = true }: UsageBarProps) {
    const isUnlimited = usage.limit === -1;
    const percentage = isUnlimited ? 0 : Math.min((usage.current / usage.limit) * 100, 100);
    const isWarning = !isUnlimited && percentage >= 80;
    const isUrgent = !isUnlimited && percentage >= 100;

    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                    {icon}
                    <span>{label}</span>
                </div>
                <span className={`font-medium ${isUrgent ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-gray-600 dark:text-gray-400'}`}>
                    {isUnlimited ? 'Unlimited' : `${usage.current} / ${usage.limit}`}
                </span>
            </div>
            
            {!isUnlimited && (
                <>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div 
                            className={`h-full transition-all duration-300 rounded-full ${
                                isUrgent ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                        />
                    </div>
                    
                    {showResetTime && usage.resetIn && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Resets {usage.resetIn}
                        </p>
                    )}
                </>
            )}
        </div>
    );
}

export function UsageDisplay({ className = '', compact = false }: UsageDisplayProps) {
    const [usage, setUsage] = useState<UserUsage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchUsage();
    }, []);

    const fetchUsage = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('kuraAuthToken');
            
            if (!token) {
                setError('Not authenticated');
                return;
            }

            const response = await fetch('/api/user/usage', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch usage');
            }

            const data = await response.json();
            setUsage(data.data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load usage');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className={`animate-pulse ${className}`}>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
                <div className="space-y-3">
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
            </div>
        );
    }

    if (error || !usage) {
        return null; // Silently fail - don't break the UI
    }

    const tierName = TIER_DISPLAY_NAMES[usage.tier];
    const showUpgradeHint = usage.tier === 'free' && (
        (usage.usage.contentSaves.current / usage.usage.contentSaves.limit) >= 0.8 ||
        (usage.usage.chatMessages.current / usage.usage.chatMessages.limit) >= 0.8 ||
        (usage.usage.learningPaths.current / usage.usage.learningPaths.limit) >= 0.8
    );

    if (compact) {
        return (
            <div className={`flex items-center gap-2 text-sm ${className}`}>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    usage.tier === 'enterprise' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' :
                    usage.tier === 'pro' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                    'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                }`}>
                    {tierName}
                </span>
                {showUpgradeHint && (
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                )}
            </div>
        );
    }

    return (
        <div className={`bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">Usage</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    usage.tier === 'enterprise' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' :
                    usage.tier === 'pro' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                    'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                }`}>
                    {tierName} Tier
                </span>
            </div>

            <div className="space-y-4">
                <UsageBar 
                    label="Content Saves" 
                    icon={<Save className="w-4 h-4" />}
                    usage={usage.usage.contentSaves}
                />
                
                <UsageBar 
                    label="Chat Messages" 
                    icon={<MessageSquare className="w-4 h-4" />}
                    usage={usage.usage.chatMessages}
                />

                {usage.tier === 'free' && (
                    <>
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                    <Library className="w-4 h-4" />
                                    <span>Library Items</span>
                                </div>
                                <span className="font-medium text-gray-900 dark:text-white">
                                    {usage.usage.totalContent.current} / {usage.usage.totalContent.limit}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                <Video className="w-4 h-4" />
                                <span>YouTube Video Length</span>
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white">
                                30 minutes max
                            </span>
                        </div>
                    </>
                )}
                
                {usage.tier === 'pro' && (
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                <Video className="w-4 h-4" />
                                <span>YouTube Video Length</span>
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white">
                                90 minutes max
                            </span>
                        </div>
                    </div>
                )}
                
                {usage.tier === 'enterprise' && (
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                <Video className="w-4 h-4" />
                                <span>YouTube Video Length</span>
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white">
                                180 minutes max
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {showUpgradeHint && (
                <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div className="flex items-start gap-2">
                        <TrendingUp className="w-4 h-4 text-amber-600 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                                Approaching limits
                            </p>
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                Upgrade to Pro for higher limits
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UsageDisplay;
